import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Avatar,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  AccountCircle,
  Edit,
  Close,
  Save,
  Email,
  Phone,
  Person,
  CalendarToday
} from '@mui/icons-material';
import SecuredLayout from '../components/layouts/SecuredLayout';
import useUserStore from '../stores/userStore';
import { alpha, useTheme } from '@mui/material/styles';

const Profile = () => {
  const theme = useTheme();
  const [isUpdate, setIsUpdate] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [user, setUser] = useState({
    id: null,
    name: "",
    email: "",
    phone: "",
    first_name: "",
    last_name: "",
    date_of_birth: "",
    is_active: false,
  });

  const { userProfile, fetchUserProfile, updateUserProfile } = useUserStore();

  // Fetch user profile on component mount
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        setIsLoading(true);
        await fetchUserProfile();
      } catch (error) {
        console.error('Error loading user profile:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load profile data',
          severity: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadUserProfile();
  }, [fetchUserProfile]);

  // Update local state when userProfile changes
  useEffect(() => {
    if (userProfile && userProfile.id) {
      setUser({
        id: userProfile.id,
        name: userProfile.name || "",
        email: userProfile.email || "",
        phone: userProfile.phone || "",
        first_name: userProfile.first_name || "",
        last_name: userProfile.last_name || "",
        date_of_birth: userProfile.date_of_birth || "",
        is_active: userProfile.is_active || false,
      });
    }
  }, [userProfile]);


  const handleEditSwitch = () => {
    setEditDialogOpen(true);
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setFieldErrors({});
  };

  const handleInputChange = (field, value) => {
    setUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleClientUpdate = async () => {
    setIsUpdate(true);
    setFieldErrors({});
    
    // Client-side validation: Check if date of birth is in the future
    if (user.date_of_birth) {
      const selectedDate = new Date(user.date_of_birth);
      const today = new Date();
      today.setHours(23, 59, 59, 999); // Set to end of today
      
      if (selectedDate > today) {
        setFieldErrors({
          date_of_birth: 'Date of birth cannot be in the future.'
        });
        setSnackbar({
          open: true,
          message: 'Date of birth cannot be in the future.',
          severity: 'error'
        });
        setIsUpdate(false);
        return;
      }
    }
    
    try {
      // Prepare payload with only the fields that exist in the API
      const payload = {
        // name: user.name, // Commented out as requested
        email: user.email,
        phone: user.phone,
        first_name: user.first_name,
        last_name: user.last_name,
        date_of_birth: user.date_of_birth
      };

      // Update user profile via API
      const result = await updateUserProfile(userProfile.id, payload);
      
      // Only show success if we actually got a successful response
      if (result) {
        setEditDialogOpen(false);
        setFieldErrors({});
        setSnackbar({
          open: true,
          message: 'Profile updated successfully!',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      
      // Parse API error response
      const errorData = error.response?.data || {};
      let errorMessage = 'Failed to update profile';
      const errors = {};
      
      // Check for field-specific errors (e.g., {"date_of_birth": ["Date of birth cannot be in the future."]})
      if (errorData && typeof errorData === 'object') {
        Object.keys(errorData).forEach(field => {
          if (Array.isArray(errorData[field]) && errorData[field].length > 0) {
            errors[field] = errorData[field][0]; // Get first error message for the field
            // Use the first field error as the main message
            if (errorMessage === 'Failed to update profile') {
              errorMessage = errorData[field][0];
            }
          } else if (typeof errorData[field] === 'string') {
            // Handle case where error is a string instead of array
            errors[field] = errorData[field];
            if (errorMessage === 'Failed to update profile') {
              errorMessage = errorData[field];
            }
          }
        });
      }
      
      // If no field errors but there's a message, use it
      if (Object.keys(errors).length === 0 && errorData.message) {
        errorMessage = errorData.message;
      }
      
      // Don't close dialog on error - let user fix the issues
      setFieldErrors(errors);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setIsUpdate(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (isLoading) {
    return (
      <SecuredLayout 
        activeTab={0} 
        pageTitle="Profile Details"
        contentCols={10}
      >
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </SecuredLayout>
    );
  }

  return (
    <SecuredLayout 
      activeTab={0} 
      pageTitle="Profile Details"
      contentCols={2}
    >
      <Container maxWidth="sm" sx={{ padding:'none'}}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Profile Header — Gradient */}
          <Card elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.15) }}>
            <Box sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.darker || '#333968'} 0%, ${theme.palette.primary.main} 100%)`,
              px: 4, py: 4,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              position: 'relative'
            }}>
              <Avatar sx={{
                width: 88, height: 88, mb: 2,
                backgroundColor: alpha('#fff', 0.2),
                border: '3px solid',
                borderColor: alpha('#fff', 0.4),
                fontSize: 40
              }}>
                <AccountCircle sx={{ fontSize: 56, color: '#fff' }} />
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff', mb: 0.5 }}>
                {user.first_name ? `${user.first_name} ${user.last_name}`.trim() : user.name || 'User Profile'}
              </Typography>
              <Typography variant="body2" sx={{ color: alpha('#fff', 0.75) }}>
                {user.email || ''}
              </Typography>
            </Box>
          </Card>

          {/* Personal Information */}
          <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.15) }}>
            {/* Card Header */}
            <Box sx={{
              px: 3, py: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              borderBottom: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.12),
              backgroundColor: alpha(theme.palette.primary.main, 0.04)
            }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.darker }}>
                Personal Information
              </Typography>
              <Tooltip title="Edit Profile">
                <IconButton
                  onClick={handleEditSwitch}
                  size="small"
                  sx={{
                    color: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.15) }
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Info Rows */}
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {[
                  { label: 'Email Address', value: user.email, icon: <Email fontSize="small" />, color: theme.palette.primary.main },
                  { label: 'Phone Number', value: user.phone, icon: <Phone fontSize="small" />, color: theme.palette.success.main },
                  { label: 'First Name', value: user.first_name, icon: <Person fontSize="small" />, color: theme.palette.info.main },
                  { label: 'Last Name', value: user.last_name, icon: <Person fontSize="small" />, color: theme.palette.secondary.main },
                  { label: 'Date of Birth', value: user.date_of_birth ? new Date(user.date_of_birth).toLocaleDateString() : null, icon: <CalendarToday fontSize="small" />, color: theme.palette.warning.main },
                ].map((field, i) => (
                  <Box key={i} sx={{
                    display: 'flex', alignItems: 'center', gap: 2,
                    p: 2, borderRadius: 2,
                    backgroundColor: alpha(field.color, 0.04),
                    border: '1px solid',
                    borderColor: alpha(field.color, 0.12),
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: alpha(field.color, 0.3),
                      backgroundColor: alpha(field.color, 0.07),
                      transform: 'translateX(4px)'
                    }
                  }}>
                    <Box sx={{
                      width: 40, height: 40, borderRadius: 2,
                      backgroundColor: alpha(field.color, 0.12),
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: field.color, flexShrink: 0
                    }}>
                      {field.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.25 }}>
                        {field.label}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {field.value || 'Not provided'}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Dialog
          open={editDialogOpen}
          onClose={handleDialogClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3, overflow: 'hidden' }
          }}
        >
          {/* Gradient Header */}
          <Box sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.darker || '#333968'} 0%, ${theme.palette.primary.main} 100%)`,
            px: 3, py: 2.5,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
              Edit Profile
            </Typography>
            <IconButton
              onClick={handleDialogClose}
              sx={{ color: '#fff', '&:hover': { backgroundColor: alpha('#fff', 0.15) } }}
            >
              <Close />
            </IconButton>
          </Box>

          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={user.email}
                onChange={(e) => {
                  handleInputChange('email', e.target.value);
                  if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: '' }));
                }}
                error={!!fieldErrors.email}
                helperText={fieldErrors.email}
                variant="outlined"
                size="small"
              />

              <TextField
                fullWidth
                label="Phone Number"
                type="tel"
                value={user.phone}
                onChange={(e) => {
                  handleInputChange('phone', e.target.value);
                  if (fieldErrors.phone) setFieldErrors(prev => ({ ...prev, phone: '' }));
                }}
                error={!!fieldErrors.phone}
                helperText={fieldErrors.phone}
                variant="outlined"
                size="small"
              />

              <TextField
                fullWidth
                label="First Name"
                value={user.first_name}
                onChange={(e) => {
                  handleInputChange('first_name', e.target.value);
                  if (fieldErrors.first_name) setFieldErrors(prev => ({ ...prev, first_name: '' }));
                }}
                error={!!fieldErrors.first_name}
                helperText={fieldErrors.first_name}
                variant="outlined"
                size="small"
              />

              <TextField
                fullWidth
                label="Last Name"
                value={user.last_name}
                onChange={(e) => {
                  handleInputChange('last_name', e.target.value);
                  if (fieldErrors.last_name) setFieldErrors(prev => ({ ...prev, last_name: '' }));
                }}
                error={!!fieldErrors.last_name}
                helperText={fieldErrors.last_name}
                variant="outlined"
                size="small"
              />

              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                value={user.date_of_birth}
                onChange={(e) => {
                  handleInputChange('date_of_birth', e.target.value);
                  if (fieldErrors.date_of_birth) setFieldErrors(prev => ({ ...prev, date_of_birth: '' }));
                }}
                error={!!fieldErrors.date_of_birth}
                helperText={fieldErrors.date_of_birth || 'Date cannot be in the future'}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: new Date().toISOString().split('T')[0] }}
                variant="outlined"
                size="small"
              />
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={handleDialogClose}
              sx={{
                flex: 1, textTransform: 'none', fontWeight: 600, borderRadius: 2,
                borderWidth: 2, '&:hover': { borderWidth: 2 }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={isUpdate}
              onClick={handleClientUpdate}
              startIcon={isUpdate ? null : <Save />}
              sx={{
                flex: 1, textTransform: 'none', fontWeight: 600, borderRadius: 2,
                boxShadow: 'none', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }
              }}
            >
              {isUpdate ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleSnackbarClose} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </SecuredLayout>
  );
};

export default Profile;
